import { lazy } from 'react';

import { AppRouteConfig } from '@/types/routes';

/**
 * Lazy-loaded route components for optimal code splitting
 *
 * Pattern explanation:
 * - Named exports: Use .then(module => ({ default: module.ComponentName }))
 * - Default exports: Direct import (e.g., AccountProcessPage)
 *
 * This ensures each route is loaded only when accessed, reducing initial bundle size
 */
const AccountProcessControlPage = lazy(() =>
  import('@/pages/account-process-control').then(module => ({
    default: module.AccountProcessControlPage,
  }))
);

// AccountProcessPage uses default export, so we can import directly
const AccountProcessPage = lazy(
  () => import('@/pages/account-process/AccountProcessPage')
);

const LoginPage = lazy(() =>
  import('@/pages/auth').then(module => ({
    default: module.LoginPage,
  }))
);

const DocumentTypesPage = lazy(() =>
  import('@/pages/document-types').then(module => ({
    default: module.DocumentTypesPage,
  }))
);

const EmailTemplatesPage = lazy(() =>
  import('@/pages/email-templates').then(module => ({
    default: module.EmailTemplatesPage,
  }))
);

const HomePage = lazy(() =>
  import('@/pages/home').then(module => ({
    default: module.HomePage,
  }))
);

const KycPage = lazy(() =>
  import('@/pages/kyc').then(module => ({
    default: module.KycPage,
  }))
);

const KycPdfTextsPage = lazy(() =>
  import('@/pages/kyc-pdf-texts').then(module => ({
    default: module.KycPdfTextsPage,
  }))
);

const LogsPage = lazy(() =>
  import('@/pages/logs').then(module => ({
    default: module.LogsPage,
  }))
);

const XmlLogsPage = lazy(() =>
  import('@/pages/xml-logs').then(module => ({
    default: module.XmlLogsPage,
  }))
);

const RolesPage = lazy(() =>
  import('@/pages/roles').then(module => ({
    default: module.RolesPage,
  }))
);

const UsersPage = lazy(() =>
  import('@/pages/users').then(module => ({
    default: module.UsersPage,
  }))
);

/**
 * Default redirect path for authenticated users
 */
export const DEFAULT_AUTHENTICATED_ROUTE = '/home';

/**
 * Default redirect path after successful login
 */
export const DEFAULT_LOGIN_REDIRECT = '/home';

/**
 * The route path for the login page in the application.
 * Used for routing to the authentication interface.
 */
export const LOGIN_PAGE = '/login';

/**
 * Centralized route configuration for the application
 * Following DRY principle and declarative pattern
 */
export const routeConfig: AppRouteConfig = {
  publicRoutes: [
    {
      path: LOGIN_PAGE,
      component: LoginPage,
      isPublic: true,
    },
  ],
  protectedRoutes: [
    {
      path: '/home',
      component: HomePage,
      // No permission required - accessible to all authenticated users
    },
    {
      path: '/communication',
      component: AccountProcessPage,
      permission: {
        entity: 'communication',
        action: 'read',
      },
    },
    {
      path: '/customers/control',
      component: AccountProcessControlPage,
      permission: {
        entity: 'control',
        action: 'read',
      },
    },
    {
      path: '/kyc',
      component: KycPage,
      permission: {
        entity: 'kyc',
        action: 'read',
      },
    },
    {
      path: '/kyc-pdf-texts',
      component: KycPdfTextsPage,
      permission: {
        entity: 'kyc-pdf-texts',
        action: 'read',
      },
    },
    {
      path: '/roles',
      component: RolesPage,
      permission: {
        entity: 'roles',
        action: 'read',
      },
    },
    {
      path: '/users',
      component: UsersPage,
      permission: {
        entity: 'users',
        action: 'read',
      },
    },
    {
      path: '/email-templates',
      component: EmailTemplatesPage,
      permission: {
        entity: 'email-templates',
        action: 'read',
      },
    },
    {
      path: '/document-types',
      component: DocumentTypesPage,
      permission: {
        entity: 'document-groups',
        action: 'read',
      },
    },
    {
      path: '/logs',
      component: LogsPage,
      permission: {
        entity: 'logs',
        action: 'read',
      },
    },
    {
      path: '/xml-logs',
      component: XmlLogsPage,
      permission: {
        entity: 'logs',
        action: 'xmls',
      },
    },
  ],
  redirectRoutes: [
    {
      path: '/',
      component: () => null,
      redirectTo: '/home',
    },
    {
      path: '*',
      component: () => null,
      redirectTo: '/home',
    },
  ],
};
